import React, { useState } from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
} from '../components/site/SubPageParts';

const IMG = '/images/site/san-tzu';

// Principles and their readings, per QA_NuV_Art of War.docx.
const PRINCIPLES = [
  {
    title: 'Battles are won or lost before they are fought.',
    body: 'War is not about impulsive action, but deliberate preparation that defines victory. It underscores the need for planning, assessing both strengths and weaknesses, and the battlefield.',
  },
  {
    title: 'Knowing Yourself and the Enemy.',
    body: 'Few principles are quoted as often as “If you know the enemy and know yourself, you need not fear the result of a hundred battles.” It speaks to the dual pillars of self-awareness and situational awareness — both indispensable for strategic mastery.',
  },
  {
    title: 'Deception as a Strategic Tool.',
    body: 'All warfare is based on deception. By mastering deception, one takes the initiative, setting the terms of engagement. The opponent is kept reactive, unsure, and ultimately disadvantaged.',
  },
  {
    title: 'Adaptability and Fluidity in Strategy.',
    body: 'Victory does not belong to the strongest or most aggressive but to the most adaptable. Sun Tzu warns: “Do not repeat the tactics which have gained you one victory.”',
  },
  {
    title: 'Significance of Terrain and Environment.',
    body: 'Understand the strategic importance of terrain. In the modern digital world, “terrain” means the internet and threat landscape. Sun Tzu notes, “He who knows the terrain and uses it with advantage will always prevail.”',
  },
  {
    title: 'Timing as a Decisive Element.',
    body: 'It is not just a tactical detail — it is a decisive element of strategic execution. Sun Tzu makes it clear that success often hinges on doing the right thing at the right moment.',
  },
  {
    title: 'Leadership Qualities.',
    body: 'It determines the effectiveness of the strategy. Even the best plans can fail under poor leadership, while a good leader can turn flawed plans into success through execution, motivation, and adaptation. Leadership, then, becomes the hinge upon which all strategic efforts swing.',
  },
];

const SanTzu = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = PRINCIPLES[activeIndex];

  return (
    <SubPage title="Art of War">
      <PageTitleBox>Art of War.</PageTitleBox>

      <PageSection className="pt-6">
        <div className="bg-black">
          <img
            src={`${IMG}/general.jpeg`}
            alt="Portrait of a warrior general in ornate armour"
            className="mx-auto h-[260px] w-auto max-w-full object-contain md:h-[430px]"
          />
        </div>
      </PageSection>

      {/* ---------------- Principles ---------------- */}
      <PageSection className="pt-10">
        <SectionHeading>Art of War Principles.</SectionHeading>

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
          <ul>
            {PRINCIPLES.map((principle, index) => (
              <li key={principle.title} className="border-b border-black/20">
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-pressed={index === activeIndex}
                  className={`w-full py-3 text-left text-[16px] transition-colors md:text-[19px] ${
                    index === activeIndex
                      ? 'text-[#4C7BA8]'
                      : 'text-black hover:text-[#4C7BA8]'
                  }`}
                >
                  {String(index + 1).padStart(2, '0')}-{principle.title}
                </button>
              </li>
            ))}
          </ul>

          <div className="lg:pt-2">
            <h3 className="text-[18px] font-bold text-black md:text-[22px]">
              {String(activeIndex + 1).padStart(2, '0')}-{active.title}
            </h3>
            <p className="mt-3 text-[16px] leading-relaxed text-black md:text-[19px]">
              {active.body}
            </p>
          </div>
        </div>
      </PageSection>

      {/* ---------------- Your enemy ---------------- */}
      {/* 9-10: the label sits above the photo as a section kicker rather than
          laid over it, so nothing is overlaid on the image at any width. */}
      <PageSection className="pt-14">
        <SectionHeading>Your enemy.</SectionHeading>
        <img
          src={`${IMG}/your-enemy.jpeg`}
          alt="A masked figure in a hood lit by a phone screen"
          className="mt-4 h-[280px] w-full object-cover small:aspect-[14/5] small:h-auto md:mt-6"
        />

        <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
          <p>
            No / SCAM was redesigned from the ground up. It reanalyzed, evaluated
            and studied the different dynamics from a human and technology
            perspective and how they intersect. The result of that approach was
            developing an &ldquo;Art of War&rdquo; inspired solution based off Sun
            Tzu principles. The more insights you have about your enemy, the more
            empowered you are to take action that keeps you safe online.
          </p>
          <p>
            The size, scope, depth and complexity of the scourge of scamming might
            convey a message that nothing can stop these unsavory characters. Not
            true. They are not without their weakness; they are not invincible. The
            key is understanding their weakness and turning it against itself,
            making their time, effort and dollar investment not worth the payoff.
          </p>
        </div>
      </PageSection>

      <ClosingBrand tagline="Designed for, Focused on and Targeting Online Scammers" />
    </SubPage>
  );
};

export default SanTzu;
